import type {
	TemplateProcess,
	GraphModification,
	Positions,
	TemplateEvent,
	TemplateTask,
	LanguageCode,
} from "@lr-notitia/notitia-designer/schemas";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { graph } from "../modules/processes/utils/graph";
import { handleGraphModification } from "./graph/utils";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { useTeamlyStore } from "./teamly";
import { useSymbolsStore } from "./symbols";
import type { Node, Link } from "@livereader/graphly-d3";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";
import { isTemplateProcess } from "@lr-notitia/notitia-model-store";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let teamlyStore: ReturnType<typeof useTeamlyStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	teamlyStore = useTeamlyStore();
	symbolsStore = useSymbolsStore();
}
export enum SidebarCategory {
	Dataobject = "data-object",
	Event = "event",
	Task = "task",
}
export type ElementType =
	| "event"
	| "task"
	| "condition_task"
	| "link"
	| "logic_gate_and"
	| "logic_gate_or"
	| "logic_gate_xor"
	| "logic_gate_not";

export const useTemplateProcessesStore = defineStore("TemplateProcesses", () => {
	init();
	let domainId = "";
	const processes = ref<TemplateProcess[]>([]);

	let loaded = ref<boolean>(false);

	function loadProcesses(dId: string): Promise<void> {
		domainId = dId;
		return new Promise(async (resolve) => {
			processes.value = sortAlphabetically(
				Object.values(graphStore.graph.nodes).filter((n) => isTemplateProcess(n)) as TemplateProcess[]
			);
			await Promise.all(processes.value.map((process) => getNodePositions(process.id)));
			resolve();
			loaded.value = true;
			api()
				.socket()
				.domain(domainId)
				.template()
				.processes()
				.on((message) => {
					switch (message.method) {
						case "create":
							if (!processes.value.find((d) => d.id === message.data.id))
								processes.value.push(message.data);
							break;
						case "update":
							processes.value = processes.value.map((process) => {
								if (process.id === message.data.id) return message.data;
								return process;
							});
							break;
						case "delete":
							processes.value = processes.value.filter((process) => process.id !== message.data);
							break;
					}
				});
		});
	}
	function clearProcesses() {
		api().socket().domain(domainId).template().processes().off();
		processes.value = [];
		domainId = "";
	}

	function getById(id: string): TemplateProcess | undefined {
		return processes.value?.find((process) => process.id === id);
	}

	function createProcess(
		conceptDataobjectId: string,
		name: string,
		description: string,
		tags: string[],
		password: string,
		language: LanguageCode
	): Promise<GraphModification> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.processes()
				.post(conceptDataobjectId, name, description, tags, password, language)
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function updateProcess(process: TemplateProcess): Promise<GraphModification> {
		if (!graphStore || domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.process(process.id)
				.put(process)
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	function removeProcess(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.process(id)
				.del()
				.then((data) => {
					handleGraphModification(data);
					resolve();
				})
				.catch(() => reject());
		});
	}

	function duplicateProcess(templateProcessId: string, templateDataobjectId: string): Promise<GraphModification> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.process(templateProcessId)
				.duplicate(templateDataobjectId)
				.then((data) => {
					const newProcess = Object.values(data).find(
						(node) => !!node && node.layer === "template" && node.type === "process"
					) as TemplateProcess;
					if (!newProcess) return reject();
					Object.values(data).forEach((node) => {
						if (!node) return;
						if (node.layer === "template" && (node.type === "task" || node.type === "event")) {
							const pos = node.context?.["nta-pos"]?.[templateProcessId];
							if (!pos) return;
							if (!nodePositions.value[newProcess.id]) nodePositions.value[newProcess.id] = {};
							nodePositions.value[newProcess.id][node.id] = {
								x: pos.x,
								y: pos.y,
								scale: pos.scale,
							};
							api()
								.socket()
								.domain(domainStore.domain.id)
								.template()
								.process(newProcess.id)
								.nodePosition()
								.send(nodePositions.value[newProcess.id]);
						}
					});

					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	function removeElement(processId: string, type: ElementType, node: Node): Promise<GraphModification> {
		if (!graphStore || !domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			const process = getById(processId);
			if (!process) return reject();
			switch (type) {
				case "event":
					process.events = process.events.filter((eventId) => eventId !== node.id);
					// TODO: remove connected links
					Promise.all([
						api().domain(domainId).template().process(processId).put(process),
						api().domain(domainId).template().event(node.id).del(),
					]).then((data) => {
						handleGraphModification(data[0]);
						resolve(data[0]);
					});
					break;
				case "task":
				case "condition_task":
				case "logic_gate_and":
				case "logic_gate_or":
				case "logic_gate_xor":
				case "logic_gate_not":
					process.tasks = process.tasks.filter((taskId) => taskId !== node.id);
					// TODO: remove connected links
					Promise.all([
						api().domain(domainId).template().process(processId).put(process),
						api().domain(domainId).template().task(node.id).del(),
					])
						.then((data) => {
							handleGraphModification(data[0]);
							resolve(data[0]);
						})
						.catch(() => reject());
					break;
			}
		});
	}
	function removeLink(link: Link): Promise<GraphModification> {
		if (!graphStore || !domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			let sourceData = graphStore.graph.nodes[typeof link.source == "string" ? link.source : link.source.id] as
				| TemplateEvent
				| TemplateTask;
			if (!sourceData) return reject();
			sourceData.activates = sourceData.activates.filter(
				(id) => id !== (typeof link.target === "string" ? link.target : link.target.id)
			);
			api()
				.domain(domainId)
				.graphs()
				.patch({ [sourceData.id]: sourceData })
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	const openedProcessId = ref<string | undefined>(undefined);
	const openedProcess = computed(() => {
		if (!openedProcessId.value) return undefined;
		return getById(openedProcessId.value);
	});
	const processGraph = computed(() => graph(openedProcessId.value || ""));
	const nodePositions = ref<{ [processId: string]: Positions }>({});

	function getNodePositions(processId: string) {
		if (!graphStore) init();
		let process = getById(processId);
		if (!process) return;
		process.tasks.forEach((taskId) => {
			let template = graphStore.template.task.byId(taskId);
			if (!template) return;
			if (!processId) return;
			if (!template.context) template.context = {};
			if (!template.context["nta-pos"]) template.context["nta-pos"] = {};
			if (!template.context["nta-pos"][processId]) template.context["nta-pos"][processId] = {};
			const pos = template.context["nta-pos"][processId];
			if (!nodePositions.value[processId]) nodePositions.value[processId] = {};
			nodePositions.value[processId][template.id] = {
				x: pos?.x || 0,
				y: pos?.y || 0,
				scale: pos?.scale || 1,
			};
		});
		process.events.forEach((eventId) => {
			let template = graphStore.template.event.byId(eventId);
			if (!template) return;
			if (!processId) return;
			if (!template.context) template.context = {};
			if (!template.context["nta-pos"]) template.context["nta-pos"] = {};
			if (!template.context["nta-pos"][processId]) template.context["nta-pos"][processId] = {};
			const pos = template.context["nta-pos"][processId];
			if (!nodePositions.value[processId]) nodePositions.value[processId] = {};
			nodePositions.value[processId][template.id] = {
				x: pos?.x || 0,
				y: pos?.y || 0,
				scale: pos?.scale || 1,
			};
		});
	}

	const matchesLinkVisibility = ref<boolean>(getLocalStorage() === null ? true : getLocalStorage() === "true");

	function sortAlphabetically(arr: TemplateProcess[]) {
		if (!symbolsStore) init();
		return arr.sort((a, b) => {
			let termA = symbolsStore.term({ id: a.refersTo, ...(a.terminology ? { terminology: a.terminology } : {}) });
			if (termA === "")
				termA = symbolsStore.term({
					id: a.refersTo,
					...(a.terminology ? { terminology: a.terminology } : {}),
					lang: "en",
				});
			let termB = symbolsStore.term({ id: b.refersTo, ...(b.terminology ? { terminology: b.terminology } : {}) });
			if (termB === "")
				termB = symbolsStore.term({
					id: b.refersTo,
					...(b.terminology ? { terminology: b.terminology } : {}),
					lang: "en",
				});

			if (termA < termB) return -1;
			else if (termA > termB) return 1;
			else return 0;
		});
	}

	function getLocalStorage(): string | null {
		return localStorage.getItem("showMatchesLink");
	}
	function setLocalStorage(show: boolean) {
		localStorage.setItem("showMatchesLink", show.toString());
	}

	function invalidAttributes(p: TemplateProcess): string[] {
		let conceptDataobject = graphStore.concept.dataobject.byId(p.dataobject);
		if (!conceptDataobject) return [];
		let attrs: string[] = [];
		p.tasks.forEach((t) => {
			let task = graphStore.template.task.byId(t);
			if (!task) return;
			task.inputAttributes.forEach((ia) => {
				if (!isValidAttribute(ia, conceptDataobject!.id) && !attrs.includes(ia)) attrs.push(ia);
			});
			task.outputAttributes.forEach((oa) => {
				if (!isValidAttribute(oa, conceptDataobject!.id) && !attrs.includes(oa)) attrs.push(oa);
			});
		});
		return attrs;
	}
	function isValidAttribute(attrId: string, conceptDataobjectId: string): boolean {
		let isValid: boolean = false;
		let conceptDataobject = graphStore.concept.dataobject.byId(conceptDataobjectId);
		if (!conceptDataobject) return false;
		if (conceptDataobject.consistsOf.includes(attrId)) isValid = true;
		if (isValid) return true;
		return conceptDataobject.composedBy.some((composedById) => {
			return isValidAttribute(attrId, composedById);
		});
	}

	function getPassword(processId: string): Promise<string> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.process(processId)
				.password()
				.get()
				.then((data) => {
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function setPassword(processId: string, password: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.process(processId)
				.password()
				.put(password)
				.then(() => {
					resolve();
				})
				.catch(() => reject());
		});
	}
	function validatePassword(processId: string, password: string): Promise<boolean> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.template()
				.process(processId)
				.password()
				.post(password)
				.then((data) => {
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	watch(
		() => matchesLinkVisibility.value,
		(newVal) => setLocalStorage(newVal),
		{ immediate: true }
	);
	watch(
		() => processes.value,
		(newVal) => {
			processes.value = sortAlphabetically(newVal);
		}
	);
	watch(
		() => processes.value.length,
		() => (processes.value = sortAlphabetically(processes.value))
	);
	watch(
		() => openedProcessId.value,
		(newId, oldId) => {
			if (oldId) {
				api().socket().domain(domainId).template().process(oldId).off();
				api()
					.socket()
					.domain(domainId)
					.template()
					.process(openedProcessId.value || "")
					.livePosition()
					.off();
			}
			if (newId && loaded.value) {
				api()
					.socket()
					.domain(domainId)
					.template()
					.process(newId)
					.on((message) => {
						const p = processes.value.find((process) => process.id === message.id);
						if (p) {
							p.refersTo = message.refersTo;
							p.reviewRequired = message.reviewRequired;
							p.dataobject = message.dataobject;
							p.events = message.events;
							p.tasks = message.tasks;
						}
					});
				api()
					.socket()
					.domain(domainId)
					.template()
					.process(openedProcessId.value || "")
					.livePosition()
					.on((pos) => {
						nodePositions.value[newId] = pos;
					});
				api()
					.socket()
					.domain(domainId)
					.template()
					.process(openedProcessId.value || "")
					.nodePosition()
					.on((pos) => {
						nodePositions.value[newId] = pos;
					});

				if (!teamlyStore) init();
				teamlyStore.getNotes(newId);
			}
		}
	);

	return {
		processes,
		loadProcesses,
		clearProcesses,
		getById,
		createProcess,
		updateProcess,
		nodePositions,
		removeProcess,
		duplicateProcess,
		removeElement,
		removeLink,
		openedProcessId,
		openedProcess,
		processGraph,
		matchesLinkVisibility,
		sortAlphabetically,
		getNodePositions,
		invalidAttributes,
		getPassword,
		setPassword,
		validatePassword,
	};
});
